/* Performance Analytics — shared presentational parts.
 * KPI row, chart panel, and data table — reused by the generic data-driven
 * dashboards AND the bespoke interactive pages (scorecards / at-risk). */
import { PAChart } from './PAChart';
import type { Kpi, Panel, TableSpec, Cell, Block } from './types';

export function KpiRow({ kpis }: { kpis: Kpi[] }) {
  return (
    <div className="pa-kpi-row">
      {kpis.map((kp, i) => (
        <div className="pa-kpi-card" key={i}>
          <div className="pa-kpi-label">{kp.label}</div>
          <div className="pa-kpi-value">{kp.value}</div>
          {kp.delta && <div className={`pa-kpi-delta ${kp.dir ?? 'flat'}`}>{kp.delta}</div>}
        </div>
      ))}
    </div>
  );
}

export function ChartPanel({ panel }: { panel: Panel }) {
  return (
    <div className="pa-panel">
      <div className="pa-panel-title">{panel.title}</div>
      {panel.sub && <div className="pa-panel-sub">{panel.sub}</div>}
      {panel.chart && (
        <div className={`pa-chart-wrap${panel.tall ? ' tall' : ''}`}>
          <PAChart spec={panel.chart} />
        </div>
      )}
      {panel.note && <div className="pa-chart-note"><b>How to read this:</b> {panel.note}</div>}
      {panel.legend && (
        <div className="pa-legend-row">
          {panel.legend.map((l, i) => (
            <div className="pa-legend-item" key={i}>
              <span className="pa-legend-swatch" style={{ background: l.color }} />{l.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CellView({ cell }: { cell: Cell }) {
  if (typeof cell === 'string') return <>{cell}</>;
  return <span className={`pa-pill ${cell.pill}`}>{cell.text}</span>;
}

export function DataTable({ table }: { table: TableSpec }) {
  return (
    <div className="pa-table-wrap">
      <table className="pa-table">
        <thead>
          <tr>{table.cols.map((c) => <th key={c}>{c}</th>)}</tr>
        </thead>
        <tbody>
          {table.rows.map((row, ri) => (
            <tr key={ri}>{row.map((cell, ci) => <td key={ci}><CellView cell={cell} /></td>)}</tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Render an ordered list of content blocks (panels, panel-rows, tables). */
export function Blocks({ blocks }: { blocks: Block[] }) {
  return (
    <>
      {blocks.map((b, i) => {
        if (b.kind === 'panel') return <ChartPanel key={i} panel={b.panel} />;
        if (b.kind === 'table') return <DataTable key={i} table={b.table} />;
        return (
          <div key={i} className={`pa-panel-row ${b.cols === '2' ? 'pa-cols-2' : 'pa-cols-2e'}`}>
            {b.panels.map((p, pi) => <ChartPanel key={pi} panel={p} />)}
          </div>
        );
      })}
    </>
  );
}
